import React, { useCallback, useMemo, useState } from 'react';
import { Box, Divider, FormControlLabel, FormGroup, Skeleton, Switch } from '@mui/material';
import classNames from 'classnames';
import { isFinite } from 'lodash';
import { useLocalStorage, useMount } from 'react-use';
import { trackEvent } from '@surfline/web-common';

import DayNightShading from 'components/DayNightShading';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import GraphTimeline from 'components/GraphTimeline';
import SwellReading from 'containers/ForecastDataVisualizer/GraphTooltips/SwellTooltip/SwellReading';
import useCustomError from 'hooks/useCustomError';
import { useUserPermissionStatus } from 'selectors/user';
import type { ConditionRating } from 'types/conditionRating';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { SwellsData } from 'types/swell';

import AdvancedSwellGraphContainer from './components/ForecastGraphAdvancedSwellContainer';

import styles from './ForecastGraphAdvancedSwell.module.scss';

const LOCAL_STORAGE_KEY = 'sl_advancedSwellGraph_toggles';
const TOOLTIP_PADDING = 3;
const TOOLTIP_WIDTH = 105;

interface Props {
  activeItem?: {
    day: number;
    hour: number;
    condition: ConditionRating;
    timestamp?: number;
    utcOffset?: number;
  };
  activeSwell: number;
  className?: string;
  daysToDisplay: number[];
  displayEnergy?: (isExpanded: boolean) => void;
  graphWidth: number;
  hoveredSwell: number;
  isMobile: boolean;
  onHoveredSwell: (index: number) => void;
  onSelectSwell: (index: number) => void;
  segmentProps?: Record<string, any>;
  sunlightTimes: SunlightTimes;
  swells: SwellsData;
  tooltipLeftPosition?: number;
}

export const ForecastGraphAdvancedSwellContent: React.FC<Props> = ({
  activeItem,
  activeSwell,
  className,
  daysToDisplay,
  displayEnergy,
  graphWidth,
  hoveredSwell,
  isMobile,
  onHoveredSwell,
  onSelectSwell,
  segmentProps,
  sunlightTimes,
  swells,
  tooltipLeftPosition,
}) => {
  const [tooltipTopPosition, setTooltipTopPosition] = useState(0);
  const [toggles, setToggles] = useLocalStorage<Record<string, boolean>>(LOCAL_STORAGE_KEY, {});
  const [isMounted, setIsMounted] = useState(false);
  const { hasCoreForecastPermissions } = useUserPermissionStatus();

  const isEnergy = useMemo(
    () => (toggles?.energy && hasCoreForecastPermissions) ?? false,
    [hasCoreForecastPermissions, toggles?.energy],
  );

  const data = useMemo(
    () => daysToDisplay?.map((day) => swells.days?.[day]).filter((day) => day),
    [daysToDisplay, swells],
  );

  const flatData = useMemo(() => data?.flat(), [data]);

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (displayEnergy && event.target.name === 'energy') {
        displayEnergy(event?.target?.checked ?? false);
      }
      trackEvent('Clicked Toggle', {
        ...segmentProps,
        toggleType: `advanced swell graph - ${event.target.name} toggle`,
        toggleStart: toggles?.[event.target.name],
        toggleEnd: event?.target?.checked,
      });
      setToggles({
        ...toggles,
        [event.target.name]: event.target.checked,
      });
    },
    [displayEnergy, segmentProps, setToggles, toggles],
  );

  const resetActiveSwell = useCallback(() => {
    onHoveredSwell(-1);
  }, [onHoveredSwell]);

  const activeSwellDataPoint = useMemo(() => {
    if (
      !isMobile &&
      activeItem?.day !== undefined &&
      isFinite(activeItem?.day) &&
      isFinite(activeItem?.hour)
    ) {
      return swells?.days?.[activeItem.day]?.[activeItem.hour]?.swells?.[hoveredSwell] ?? null;
    }

    return null;
  }, [activeItem?.day, activeItem?.hour, hoveredSwell, isMobile, swells?.days]);

  const swellTooltipLeftPosition = useMemo(() => {
    const leftPosition = tooltipLeftPosition ?? 0;
    if (leftPosition >= graphWidth - TOOLTIP_WIDTH) {
      // move tooltip to left side of scrubber bar to prevent overflowing the graphs
      return leftPosition - TOOLTIP_WIDTH - TOOLTIP_PADDING;
    }
    return leftPosition + TOOLTIP_PADDING;
  }, [graphWidth, tooltipLeftPosition]);

  useMount(() => {
    setIsMounted(true);
  });

  // Show loading skeleton during SSR and while data is loading to prevent hydration mismatch
  return swells?.loading || !isMounted ? (
    <Skeleton
      data-testid="advanced-swell-graph-loading"
      variant="rectangular"
      className={styles.loading}
    />
  ) : (
    <div
      className={classNames(styles.wrapper, className)}
      data-testid="advanced-swell-graph"
      onMouseLeave={resetActiveSwell}
    >
      {hasCoreForecastPermissions && (
        <FormGroup
          className={classNames(styles.formGroup, 'sl-form-group')}
          onChange={handleToggleChange}
          row
        >
          <FormControlLabel
            className={styles.toggle}
            control={<Switch defaultChecked={toggles?.energy} />}
            data-testid="advanced-swell-graph-toggle"
            label="Order by Energy"
            name="energy"
          />
        </FormGroup>
      )}
      <Box className={styles.content}>
        {activeSwellDataPoint && activeSwellDataPoint?.period > 0 && swellTooltipLeftPosition && (
          <Box
            className={styles.inlineSwellTooltipContainer}
            data-testid="swell-graph-tooltip"
            sx={{
              left: swellTooltipLeftPosition,
              top: tooltipTopPosition,
            }}
          >
            <SwellReading
              activeSwell={activeSwellDataPoint.index}
              applySwellColors
              swell={activeSwellDataPoint}
              units={swells.units}
              containerClassName={styles.inlineSwellTooltip}
            />
          </Box>
        )}
        <AdvancedSwellGraphContainer
          activeItem={activeItem}
          activeSwell={activeSwell}
          data={flatData}
          hoveredSwell={hoveredSwell}
          isEnergy={isEnergy}
          numberOfTicks={5}
          onHoveredSwell={onHoveredSwell}
          onSelectSwell={onSelectSwell}
          overallMaxHeight={swells.overallMaxSwellHeight}
          setTooltipTopPosition={setTooltipTopPosition}
          units={swells.units?.swellHeight}
        />
        {daysToDisplay.map((day: number, dayIndex) => (
          <Box key={day} width="100%" display="flex">
            <Divider
              className={classNames(dayIndex !== 0 && styles.divider)}
              orientation="vertical"
              flexItem
              sx={{ border: 'none' }}
            />
            <div className={classNames(styles.dayWrapper)} key={daysToDisplay[dayIndex]}>
              <div
                className={classNames([styles.dayNightShading, 'day-night-shading'])}
                data-testid="day-night-shading-day"
              >
                <DayNightShading
                  key={daysToDisplay[dayIndex]}
                  sunlightTimes={sunlightTimes}
                  numDays={daysToDisplay.length}
                  forecastDayIndex={day}
                  columnIndex={dayIndex}
                  isPaywalled={false}
                />
              </div>
              {swells.days?.[daysToDisplay[dayIndex]] ? (
                <GraphTimeline day={swells.days[daysToDisplay[dayIndex]] as any} showMarkers />
              ) : null}
            </div>
          </Box>
        ))}
      </Box>
    </div>
  );
};

const ForecastGraphAdvancedSwell: React.FC<Props> = (props) => {
  const {
    swells: { error },
  } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-advanced-swell"
      render={() => (
        <Box className={styles.error} data-testid="advanced-swell-graph-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <ForecastGraphAdvancedSwellContent {...props} />
    </ErrorBoundary>
  );
};

export default ForecastGraphAdvancedSwell;
